import { documentsBySlug } from '../data/documents';
import { RouteLink } from '../route';
import PortalLayout from './PortalLayout';

function Figure({ figure }) {
  if (!figure) {
    return null;
  }

  return (
    <figure className="doc-figure">
      <img src={figure.src} alt={figure.alt} />
      {figure.caption ? <figcaption>{figure.caption}</figcaption> : null}
    </figure>
  );
}

function ImageStrip({ items }) {
  if (!items?.length) {
    return null;
  }

  return (
    <div className="image-strip">
      {items.map((item) => (
        <figure className="image-strip-card" key={item.src}>
          <img src={item.src} alt={item.alt} />
          {item.caption ? <figcaption>{item.caption}</figcaption> : null}
        </figure>
      ))}
    </div>
  );
}

function StepList({ steps, ordered = true }) {
  if (!steps?.length) {
    return null;
  }

  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <ListTag className={ordered ? 'doc-steps' : 'content-bullets'}>
      {steps.map((step, index) => (
        <li key={`${index}-${step}`}>{step}</li>
      ))}
    </ListTag>
  );
}

function DetailGrid({ items }) {
  if (!items?.length) {
    return null;
  }

  return (
    <div className="detail-grid">
      {items.map((item) => (
        <article className="detail-card" key={item.title}>
          <h4>{item.title}</h4>
          {item.body?.map((paragraph, index) => (
            <p key={`${item.title}-${index}`}>{paragraph}</p>
          ))}
          <StepList steps={item.bullets} ordered={false} />
        </article>
      ))}
    </div>
  );
}

function RichSection({ document, section, index }) {
  return (
    <article className="content-card content-card-rich" id={section.id}>
      <div className="section-heading-row">
        <div>
          <p className="section-eyebrow">
            {document.title} / Section {String(index + 1).padStart(2, '0')}
          </p>
          <h3>{section.title}</h3>
        </div>
      </div>

      {section.body?.map((paragraph, paragraphIndex) => (
        <p key={`${section.id}-${paragraphIndex}`}>{paragraph}</p>
      ))}

      <StepList steps={section.steps} />
      <StepList steps={section.bullets} ordered={false} />
      <Figure figure={section.figure} />
      <ImageStrip items={section.gallery} />
      <DetailGrid items={section.detailCards} />

      {section.subsections?.length ? (
        <div className="subsection-stack">
          {section.subsections.map((subsection) => (
            <section className="subsection-card" id={subsection.id} key={subsection.id}>
              <h4>{subsection.title}</h4>
              {subsection.body?.map((paragraph, paragraphIndex) => (
                <p key={`${subsection.id}-${paragraphIndex}`}>{paragraph}</p>
              ))}
              <StepList steps={subsection.steps} />
              <StepList steps={subsection.bullets} ordered={false} />
              <Figure figure={subsection.figure} />
              <ImageStrip items={subsection.gallery} />
              <DetailGrid items={subsection.detailCards} />
            </section>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export default function DocumentPage({ document }) {
  const currentDocument = documentsBySlug[document.slug] || document;
  const relatedDocuments = (document.related || [])
    .map((slug) => documentsBySlug[slug])
    .filter(Boolean);

  return (
    <PortalLayout currentDocument={document}>
      <section className="hero-card">
        <div className="hero-copy">
          <div className="hero-badge-row">
            <span className="hero-badge">{currentDocument.category}</span>
          </div>

          <h1>{document.title}</h1>
          <p className="hero-summary">{document.description}</p>

          <div className="hero-actions">
            <RouteLink className="action-primary" to="/">
              Back to Portal
            </RouteLink>
          </div>
        </div>

        <div className="hero-metrics">
          {document.metrics.map((metric) => (
            <article className="metric-card" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="workflow-card">
        <div className="section-heading-row">
          <div>
            <p className="section-eyebrow">Execution Flow</p>
            <h2>Recommended workflow for this module</h2>
          </div>
        </div>

        <div className="workflow-list">
          {document.workflow.map((step, index) => (
            <div className="workflow-step" key={`${document.slug}-${index}`}>
              <span className="workflow-index">{String(index + 1).padStart(2, '0')}</span>
              <p>{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-grid">
        {document.sections.map((section, index) => (
          <RichSection document={document} section={section} index={index} key={section.id} />
        ))}
      </section>

      {relatedDocuments.length ? (
        <section className="related-card">
          <div className="section-heading-row">
            <div>
              <p className="section-eyebrow">Related Modules</p>
              <h2>Continue through connected workflows</h2>
            </div>
          </div>

          <div className="related-grid">
            {relatedDocuments.map((item) => (
              <RouteLink className="related-item" key={item.slug} to={item.route}>
                <span className="related-meta">{item.category}</span>
                <strong>{item.title}</strong>
                <p>{item.description}</p>
              </RouteLink>
            ))}
          </div>
        </section>
      ) : null}
    </PortalLayout>
  );
}
